import { inject, Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { ToastService } from './toast.service';
import { v4 } from 'uuid';
import { TranslateService } from '@ngx-translate/core';
import { formatCurrency } from '@common-helpers/currency';
import { availableCurrencies, Currency } from '@common-types/currency';
import { CurrencyTotal } from '@common-helpers/customer-stats';
import { debugLogger } from '@common-helpers/debug-logger';
@Injectable({ providedIn: 'root' })
export class Utils {
    auth = inject(AuthService);
    toast = inject(ToastService);
    translate = inject(TranslateService);
    currentUser = this.auth.currentUser();

    get userInitials(): string {
        if (this.currentUser) {
            let firstName = this.currentUser?.firstName ?? '';
            let lastName = this.currentUser?.lastName ?? '';

            return firstName[0] + lastName[0];
        }
        return '';
    }

    uuid(): string {
        return v4();
    }

    numberFormat(value: number): string {
        let split = (value: string): string => {
            let split = value.split(',');
            if (split.length == 3) {
                return `${split[0]} ${split[1]}`;
            }
            if (split.length == 4) {
                return `${split[0]} ${split[1]} ${split[2]}`;
            }
            if (split.length == 5) {
                return `${split[0]} ${split[1]} ${split[2]} ${split[3]}`;
            } else {
                return split[0];
            }
        };

        let formattedValue = value.toLocaleString('en', { minimumFractionDigits: 2 }).replace(',', ' ').replace('.', ',');
        return split(formattedValue);
    }

    /**
     * Formats an amount in a currency. Venue amounts should pass the venue's currency; the user's
     * own setting is only a fallback, and without any currency the plain number is shown.
     */
    currencyFormat(number: number, currency?: Currency | string | null): string {
        const code = String(currency || this.auth.currentUser()?.settings?.currency || '').toLowerCase();
        const value = typeof number === 'number' && isFinite(number) ? Math.round(number * 100) / 100 : 0;

        if (availableCurrencies.some((c) => c.code === code)) {
            // The shared formatter treats 0 as "no value" and returns an empty string.
            return formatCurrency(code as Currency, value === 0 ? '0' : value);
        }

        return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    /** One amount per currency, e.g. "12 400 kr · €310,00". */
    currencyTotals(totals: readonly CurrencyTotal[], field: 'lifetimeValue' | 'averageValue'): string {
        if (!totals.length) {
            return this.currencyFormat(0);
        }
        return totals.map((total) => this.currencyFormat(total[field], total.currency)).join(' · ');
    }

    async copyToClipboard(text: string): Promise<boolean> {
        try {
            await navigator.clipboard.writeText(text);
            debugLogger.info('Copied to clipboard!');
            this.toast.showInfo(`${text} ${this.translate.instant('COPIED_TO_CLIPBOARD')}`);
            return true;
        } catch (err) {
            debugLogger.error('Failed to copy:', err);
            return false;
        }
    }
}
